import React, { useEffect, useMemo, useState } from "react";
import ComboboxAutocomplete from "./combobox_autocomplete";
import { getWeeklyWorkingHourCompound } from "../services/api_working_hours";

export default function WeeklyWorkingHoursCompoundSelect({
  token,
  permission = "payrolls",

  // OU opzionale per caricare i turni tramite API
  sourceOu = null,

  // Array opzionale già caricato dal componente padre
  sourceItems = null,

  value = null,
  onChange,
  placeholder = "Cerca e seleziona un modello turno...",
  disabled = false,
  zIndex = "z-[80]",

  // opzionale: se passato, override del rendering
  renderOption,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
    
  const ouId = sourceOu?.id ?? null;
  const hasSourceItems = Array.isArray(sourceItems);

  /*
   * Usa direttamente i dati forniti dal parent.
   * In questo caso non serve effettuare una nuova chiamata API.
   */
  useEffect(() => {
    if (!hasSourceItems) return;

    setItems(sourceItems);
    setLoading(false);
  }, [hasSourceItems, sourceItems]);

  /*
   * Caricamento tramite API solo quando sourceItems non è stato passato.
   */
  useEffect(() => {
    if (!token || hasSourceItems) return;

    const controller = new AbortController();
    const { signal } = controller;

    const fetchItems = async () => {
      setLoading(true);

      try {
        const response = await getWeeklyWorkingHourCompound(
          token,
          ouId,
          null,
          permission,
          signal
        );

        const results = Array.isArray(response)
          ? response
          : Array.isArray(response?.results)
            ? response.results
            : [];

        setItems(results);
      } catch (error) {
        if (
          error?.name !== "CanceledError" &&
          error?.name !== "AbortError" &&
          error?.code !== "ERR_CANCELED"
        ) {
          console.error(
            "Errore durante il caricamento dei turni settimanali:",
            error
          );
        }

        if (!signal.aborted) {
          setItems([]);
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchItems();

    return () => controller.abort();
  }, [token, permission, ouId, hasSourceItems]);

  const options = useMemo(() => {
    return (items || []).map((workingHour) => {
      const ouLabel =
        workingHour?.ou?.name ||
        workingHour?.ou_name ||
        "Generico";

      const rawName = workingHour?.name || "—";

      return {
        ...workingHour,
        name: rawName,
        __rawName: rawName,
        __ouLabel: ouLabel,
      };
    });
  }, [items]);

  const defaultRender = (shift) => (
    <div className="flex w-full items-center gap-3">
      {/* SINISTRA: nome turno */}
      <span className="min-w-0 flex-1 truncate text-sm font-normal text-gray-700">
        {shift?.__rawName || shift?.name || "—"}
      </span>

      {/* DESTRA: badge OU */}
      <span className="ml-auto max-w-[40%] shrink-0 truncate rounded-full border border-indigo-300 bg-indigo-50 px-2.5 py-0.5 text-[11px] font-semibold text-indigo-700">
        {shift?.__ouLabel || "Generico"}
      </span>
    </div>
  );

  return (
    <div className="w-full border border-gray-200 rounded-lg bg-white shadow-sm">
      <ComboboxAutocomplete
        options={options}
        value={value}
        onChange={(option) => onChange?.(option || null)}
        placeholder={loading ? "Caricamento..." : placeholder}
        bgActiveColor="bg-indigo-50"
        textActiveColor="text-gray-900"
        positionOptions="absolute"
        zIndex={zIndex}
        borderClass="border-gray-200 rounded-lg"
        borderInput={disabled ? "opacity-60 pointer-events-none" : ""}
        displayValue={(option) =>
          option?.__rawName || option?.name || ""
        }
        // render override se passato dal parent
        renderOption={renderOption || defaultRender}
      />
    </div>
  );
}